import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ChatSettingStyles } from '../../../services/chat.service';
import { solidFallback } from '../../../components/color-input/color-value';
import { AidLoaderStyle, aidCssVariables, firstColor, resolveAidStyle, toHex } from '../aid-style';
import { GLITTER_REVEAL_SHARE, GLITTER_RING_AT_SHARE, ORBI_RING_AT, OrbiLoader, startOrbiLoader } from '../orbi-loader';

/** Beispiel-Lernhilfen der Vorschau, in Blätter-Reihenfolge. */
type AidSample = 'tiles' | 'points' | 'calc' | 'flow' | 'cards';
const SAMPLES: readonly AidSample[] = ['tiles', 'points', 'calc', 'flow', 'cards'];
/** Text der Antwortblase über der jeweiligen Lernhilfe (i18n unter preview.aid). */
const SAMPLE_INTRO: Record<AidSample, string> = {
  tiles: 'intro',
  points: 'pointsIntro',
  calc: 'between',
  flow: 'flowIntro',
  cards: 'cardsIntro',
};

/** Ladezeit vor dem Reveal — Code Orbi braucht etwas, bis er steht. */
const LOAD_MS: Record<AidLoaderStyle, number> = { 'code-orbi': 2600, 'pixel-glitter': 1400 };
/** Reveal wie im Widget (`ORBI_REVEAL_MS` in learning-block.component.ts). */
const REVEAL_MS = 1500;
/** Höhe der Ladekarte (= `:host(.lb--loading)` im Widget). */
const LOADING_HEIGHT = 132;

/**
 * Vorschau des Reiters „Lernhilfen“ im Chat-Styling: eine Antwortblase in den
 * Farben des Stylings mit EINER Lernhilfe (Kacheln, Aufzählung, Rechner, Ablauf
 * oder Karteikarten — per Pfeil umschaltbar). Die Ladeanimation läuft einmal, wie im Chat: beim Öffnen
 * des Reiters, beim Blättern und beim Wechsel der Ladeanimation — danach
 * steht die Lernhilfe (Marcel, 20.10.2026).
 *
 * Die Farben rechnet `resolveAidStyle` (aid-style.ts) — dieselbe Datei wie im
 * chat-widget, kopiert von scripts/sync_learning_blocks.py; der Lader ist eine
 * Kopie von chat-widget/…/orbi-loader.ts, der Ablauf von Laden und Reveal folgt
 * `revealWithOrbi` in learning-block.component.ts. Das Markup ist ein Nachbau
 * der Widget-Bausteine (`app-lb-*`): Klassen- und Variablennamen wie dort.
 */
@Component({
  selector: 'app-aid-style-preview',
  templateUrl: './aid-style-preview.component.html',
  styleUrl: './aid-style-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AidStylePreviewComponent implements OnChanges, OnDestroy {
  @Input() styles: Partial<ChatSettingStyles> = {};
  /** Reiter „Lernhilfen“ ist sichtbar — erst dann wird geladen (und gemessen). */
  @Input() active = false;
  /** Interface Liquid: die Lernhilfe steht im Widget in der Canvas — die
   *  Vorschau zeigt den Canvas-Rahmen drumherum (Look für die Apps). */
  @Input() frame: 'classic' | 'liquid' | null = null;

  @ViewChild('card', { static: true }) cardEl!: ElementRef<HTMLElement>;
  @ViewChild('content', { static: true }) contentEl!: ElementRef<HTMLElement>;
  @ViewChild('loaderCanvas', { static: true }) loaderCanvas!: ElementRef<HTMLCanvasElement>;

  /** `--aid-*` für den Host (aus dem Styling abgeleitet bzw. gesetzt). */
  vars: Record<string, string> = {};
  bubbleFill = '#3B82F6';
  bubbleText = '#ffffff';
  font = 'Arial, sans-serif';
  loader: AidLoaderStyle = 'code-orbi';
  /** Reglerwert der Beispielrechnung (Prozentpunkte). */
  rate = 0.3;

  readonly samples = SAMPLES;
  index = 0;
  phase: 'loading' | 'revealing' | 'done' = 'done';
  contentIn = true;

  private accent = '#003598';
  private surface = '#ffffff';
  private running: OrbiLoader | null = null;
  private timers: ReturnType<typeof setTimeout>[] = [];

  constructor(
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  get sample(): AidSample {
    return SAMPLES[this.index];
  }

  get introKey(): string {
    return `pages.chatStyling.preview.aid.${SAMPLE_INTRO[this.sample]}`;
  }

  /** Frage, die die Liquid-Animation zur jeweiligen Lernhilfe tippt. */
  get questionKey(): string {
    return `pages.chatStyling.preview.aid.ask.${this.sample}`;
  }

  private get liquid(): boolean {
    return this.frame === 'liquid';
  }

  ngOnChanges(changes: SimpleChanges): void {
    const s = this.styles ?? {};
    const resolved = resolveAidStyle(s as Record<string, unknown>);
    const radius = parseFloat(resolved.values.aidCornerRadius) || 20;
    this.font = `${s.fontFamily || 'Arial'}, sans-serif`;
    this.bubbleFill = (s.secondaryColor as string) || '#3B82F6';
    this.bubbleText = solidFallback(s.botTextColor as string) || '#ffffff';
    this.vars = {
      ...aidCssVariables(resolved),
      '--aid-radius-inner': `${Math.max(0, Math.round(radius * 0.7))}px`,
      '--aid-font': this.font,
      '--aid-bubble-text': this.bubbleText,
    };
    this.accent = resolved.values.aidAccentColor;
    this.surface = toHex(firstColor(resolved.values.aidCardBackground) ?? [255, 255, 255]);

    const loaderChanged = resolved.loader !== this.loader;
    this.loader = resolved.loader;
    if (changes['active'] && !this.active) this.finish();
    // Liquid: geladen wird erst, wenn die Animation die Antwort zeigt
    // (onDemoAnswer) — nur ein Wechsel der Ladeanimation spielt sofort neu.
    else if (this.active && (changes['active'] || loaderChanged) && !(this.liquid && changes['active']))
      this.play();
  }

  /** Liquid-Animation: die Frage ist gesendet, die Lernhilfe kommt als Antwort. */
  onDemoAnswer(): void {
    this.play();
  }

  ngOnDestroy(): void {
    this.clear();
  }

  /** Blättern: vorige/nächste Lernhilfe, im Kreis — lädt sie neu. */
  go(step: number): void {
    this.index = (this.index + step + SAMPLES.length) % SAMPLES.length;
    // Liquid: die Animation tippt die nächste Frage und meldet die Antwort.
    if (this.liquid) {
      this.cdr.markForCheck();
      return;
    }
    this.play();
  }

  /** Ergebnis der Beispielrechnung: 38 Billionen $ Schulden × Prozentpunkte. */
  get result(): string {
    return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(38000 * this.rate / 100);
  }

  get fillPercent(): number {
    return ((this.rate - 0.1) / 0.9) * 100;
  }

  /** Einmal laden und aufdecken, wie eine neue Lernhilfe im Chat. */
  private play(): void {
    this.clear();
    if (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.finish();
      return;
    }
    this.phase = 'loading';
    this.contentIn = false;
    this.cdr.detectChanges();
    const glitter = this.loader === 'pixel-glitter';
    this.running = this.zone.runOutsideAngular(() =>
      startOrbiLoader(this.loaderCanvas.nativeElement, {
        accent: this.accent,
        surface: this.surface,
        stageHeight: LOADING_HEIGHT,
        orbi: !glitter,
      }),
    );
    if (!this.running) {
      this.finish();
      return;
    }
    this.later(() => this.reveal(glitter), LOAD_MS[this.loader]);
  }

  /** Ablauf wie `revealWithOrbi` im Widget: Ring gibt den Inhalt frei, die Karte wächst. */
  private reveal(glitter: boolean): void {
    const card = this.cardEl.nativeElement;
    const target = this.contentEl.nativeElement.offsetHeight;
    card.style.height = `${card.offsetHeight}px`;
    void card.offsetHeight; // Startwert festschreiben, sonst springt die Höhe
    card.style.setProperty('--cover-h', `${Math.max(target, card.offsetHeight)}px`);
    this.phase = 'revealing';
    this.cdr.detectChanges();
    this.running?.reveal(REVEAL_MS);

    const ringAt = REVEAL_MS * (glitter ? GLITTER_RING_AT_SHARE : ORBI_RING_AT);
    const total = glitter ? REVEAL_MS * GLITTER_REVEAL_SHARE : REVEAL_MS;
    this.later(() => {
      card.style.height = `${target}px`;
      this.contentIn = true;
    }, ringAt);
    this.later(() => this.finish(), total + 30);
  }

  /** Lernhilfe steht: Lader weg, Karte in natürlicher Höhe. */
  private finish(): void {
    this.clear();
    this.phase = 'done';
    this.contentIn = true;
    this.cdr.markForCheck();
  }

  private clear(): void {
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.running?.stop();
    this.running = null;
    const card = this.cardEl?.nativeElement;
    card?.style.removeProperty('height');
    card?.style.removeProperty('--cover-h');
  }

  private later(fn: () => void, ms: number): void {
    this.timers.push(
      setTimeout(() => {
        fn();
        this.cdr.markForCheck();
      }, ms),
    );
  }
}
